<template>
    <div class="wrapper flex flex-column justify-content-center align-items-center p-4 w-full h-full">
        <CustomBreadcrumb class="flex align-self-start"
            :items="[{ label: 'Profile Settings' }, { label: 'Parents' }]" />
        <span class="H6-Medium block text-center mt-6">Add details about your parents/guardians here</span>

        <form class="md:col-11 md:p-5 mt-6">
            <div class="w-full col-12 grid">
                <div class="col-12 md:col-6 md:pr-3 gap-3 flex flex-column">
                    <div class="input-field-container">
                        <label for="parentName" class="Lable">Parents/Guardians name</label>
                        <InputText id="parentName" v-model="form.guardians[0].name" class="w-full"
                            :class="{ 'p-invalid': errors.parentName }" variant="filled" size="small" />
                        <small class="error-msg">{{ errors.parentName }}</small>
                    </div>

                    <div class="input-field-container">
                        <label for="parentNumber" class="Lable">Parents/Guardians Number</label>
                        <div class="flex gap-2 w-full">
                            <Select v-model="form.guardians[0].phoneCountryCode" :options="countryCodeOptions"
                                optionLabel="name" optionValue="code" placeholder="Code" class="phone-country-select" />
                            <InputText id="parentNumber" v-model="form.guardians[0].phoneNumber" type="tel"
                                class="w-full" :class="{ 'p-invalid': errors.parentNumber }" variant="filled"
                                size="small" maxlength="15" @keypress="onlyAllowNumbers" />
                        </div>
                        <small class="error-msg">{{ errors.parentNumber }}</small>
                    </div>

                    <div class="input-field-container">
                        <label for="parentEmail" class="Lable">Parents/Guardians email</label>
                        <InputText id="parentEmail" v-model="form.guardians[0].email" class="w-full"
                            :class="{ 'p-invalid': errors.parentEmail }" variant="filled" size="small" />
                        <small class="error-msg">{{ errors.parentEmail }}</small>
                    </div>

                    <div class="relative pb-3">
                        <div class="flex align-items-center mt-2">
                            <Checkbox v-model="form.guardians[0].isPrimary" :binary="true" inputId="primary1"
                                class="orange-checkbox" @change="setPrimary(0)" :disabled="!showSecondary" />
                            <label for="primary1" class="ml-2 text-sm">Primary Parents/Guardian</label>
                        </div>
                        <small v-if="errors.primaryGuardian" class="error-msg" style="margin-left: 28px;">{{
                            errors.primaryGuardian }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3 gap-3 flex flex-column">
                    <template v-if="showSecondary">
                        <div class="input-field-container">
                            <label for="secName" class="Lable">Parents/Guardians name</label>
                            <InputText id="secName" v-model="form.guardians[1].name" class="w-full"
                                :class="{ 'p-invalid': errors.secName }" variant="filled" size="small" />
                            <small class="error-msg">{{ errors.secName }}</small>
                        </div>
                        <div class="input-field-container">
                            <label for="secNumber" class="Lable">Parents/Guardians Number</label>
                            <div class="flex gap-2 w-full">
                                <Select v-model="form.guardians[1].phoneCountryCode" :options="countryCodeOptions"
                                    optionLabel="name" optionValue="code" placeholder="Code"
                                    class="phone-country-select" />
                                <InputText id="secNumber" v-model="form.guardians[1].phoneNumber" type="tel"
                                    class="w-full" :class="{ 'p-invalid': errors.secNumber }" variant="filled"
                                    size="small" maxlength="15" @keypress="onlyAllowNumbers" />
                            </div>
                            <small class="error-msg">{{ errors.secNumber }}</small>
                        </div>
                        <div class="input-field-container">
                            <label for="secEmail" class="Lable">Parents/Guardians email</label>
                            <InputText id="secEmail" v-model="form.guardians[1].email" class="w-full"
                                :class="{ 'p-invalid': errors.secEmail }" variant="filled" size="small" />
                            <small class="error-msg">{{ errors.secEmail }}</small>
                        </div>

                        <div v-if="showSecondary" class="flex align-items-start justify-content-between w-full mt-2">
                            <div class="flex flex-column">
                                <div class="flex align-items-start">
                                    <Checkbox v-model="form.guardians[1].isPrimary" :binary="true" inputId="primary2"
                                        class="orange-checkbox" @change="setPrimary(1)" />
                                    <label for="primary2" class="ml-2 text-sm cursor-pointer">Primary
                                        Parents/Guardian</label>
                                </div>
                                <small v-if="errors.primaryGuardian" class="error-msg"
                                    style="margin-left: 28px; position: static; display: block;">
                                    {{ errors.primaryGuardian }}
                                </small>
                            </div>
                            <div class="flex align-items-start">
                                <Button label="Remove" class="p-button-outlined p-button-danger p-button-sm"
                                    @click="removeSecondary" />
                            </div>
                        </div>
                    </template>
                </div>

                <div class="col-12 md:pr-3">
                    <div class="flex justify-content-between align-items-start mt-4">
                        <div>
                            <Button label="Save" class="orange-button w-full md:w-auto"
                                :disabled="!isFormValid || !hasChanges" @click.prevent="handleSubmit" size="small" />

                            <div v-if="!showSecondary" class="flex align-items-center cursor-pointer mt-4"
                                @click="showSecondary = true">
                                <i class="pi pi-plus text-xs add-link p-2 border-round"></i>
                                <span class="font-medium ml-2">Add parent/ guardian details</span>
                            </div>
                        </div>

                        <!-- <div v-if="showSecondary">
                            <Button label="Remove" class="p-button-outlined p-button-danger"
                                @click="removeSecondary" />
                        </div> -->
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import CustomBreadcrumb from '@/components/CustomBreadcrumb.vue';
import { computed, ref, defineEmits, defineProps, watch } from 'vue';
import { Button, InputText } from "primevue";
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import { countryCodeOptions } from '@/utils/countryCodeOptions';

const emit = defineEmits(["update-prfile"]);
const props = defineProps({
    profile: { type: Object, required: true }
});

const showSecondary = ref(false);
const form = ref({
    guardians: [
        { name: "", phoneCountryCode: "+94", phoneNumber: "", email: "", isPrimary: true },
        { name: "", phoneCountryCode: "+94", phoneNumber: "", email: "", isPrimary: false }
    ]
});

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Computed Error Object for UI Feedback
const errors = computed(() => {
    const e = {};
    const g1 = form.value.guardians[0];
    const g2 = form.value.guardians[1];

    // Primary Validations
    if (g1.name && g1.name.length < 3) e.parentName = "Name is too short";
    if (g1.phoneNumber && g1.phoneNumber.length < 6) e.parentNumber = "Invalid phone number";
    if (g1.email && !isValidEmail(g1.email)) e.parentEmail = "Invalid email address";

    // Secondary Validations
    if (showSecondary.value) {
        if (g2.name && g2.name.length < 3) e.secName = "Name is too short";
        if (g2.phoneNumber && g2.phoneNumber.length < 6) e.secNumber = "Invalid phone number";
        if (g2.email && !isValidEmail(g2.email)) e.secEmail = "Invalid email address";
        if (!g1.isPrimary && !g2.isPrimary) e.primaryGuardian = "Please select a primary guardian";
    }

    return e;
});

const isFormValid = computed(() => {
    const g1 = form.value.guardians[0];
    const primaryOk = g1.name.trim() &&
        g1.phoneNumber.length >= 6 &&
        isValidEmail(g1.email);

    if (!showSecondary.value) return primaryOk && Object.keys(errors.value).length === 0;

    const g2 = form.value.guardians[1];
    const secondaryOk = g2.name.trim() &&
        g2.phoneNumber.length >= 6 &&
        isValidEmail(g2.email);

    return primaryOk && secondaryOk && Object.keys(errors.value).length === 0;
});

const parsePhoneFromProfile = (contactNumber) => {
    if (!contactNumber) return { phoneCountryCode: "+94", phoneNumber: "" };
    const numStr = String(contactNumber);
    if (numStr.startsWith("+")) {
        const sortedCodes = [...countryCodeOptions.map(c => c.code)].sort((a, b) => b.length - a.length);
        for (const code of sortedCodes) {
            if (numStr.startsWith(code)) {
                return { phoneCountryCode: code, phoneNumber: numStr.slice(code.length) };
            }
        }
    }
    return { phoneCountryCode: "+94", phoneNumber: numStr };
};

const hasChanges = computed(() => {
    const p = props.profile || {};
    const pGuardians = p.guardians || [];
    const fGuardians = form.value.guardians;

    const g1 = pGuardians[0] || {};
    const g2 = pGuardians[1] || {};

    const f1 = fGuardians[0];
    const f2 = fGuardians[1];

    const g1Phone = parsePhoneFromProfile(g1.contactNumber);
    if (f1.name !== (g1.name || "") || f1.phoneCountryCode !== g1Phone.phoneCountryCode || f1.phoneNumber !== g1Phone.phoneNumber || f1.email !== (g1.email || "") || f1.isPrimary !== (g1.isPrimary || false)) return true;

    if (showSecondary.value) {
        const g2Phone = parsePhoneFromProfile(g2.contactNumber);
        if (f2.name !== (g2.name || "") || f2.phoneCountryCode !== g2Phone.phoneCountryCode || f2.phoneNumber !== g2Phone.phoneNumber || f2.email !== (g2.email || "") || f2.isPrimary !== (g2.isPrimary || false)) return true;
    }

    return showSecondary.value !== (pGuardians.length > 1);
});

watch(() => props.profile, (p) => {
    if (p && p.guardians) {
        const g1 = p.guardians[0] || { name: "", contactNumber: "", email: "", isPrimary: true };
        const g2 = p.guardians[1] || { name: "", contactNumber: "", email: "", isPrimary: false };

        const g1Phone = parsePhoneFromProfile(g1.contactNumber);
        const g2Phone = parsePhoneFromProfile(g2.contactNumber);

        form.value.guardians = [
            { name: g1.name || "", phoneCountryCode: g1Phone.phoneCountryCode, phoneNumber: g1Phone.phoneNumber, email: g1.email || "", isPrimary: g1.isPrimary },
            { name: g2.name || "", phoneCountryCode: g2Phone.phoneCountryCode, phoneNumber: g2Phone.phoneNumber, email: g2.email || "", isPrimary: g2.isPrimary }
        ];
        if (p.guardians.length > 1) showSecondary.value = true;
        if (!showSecondary.value) {
            form.value.guardians[0].isPrimary = true;
        }
    }
}, { immediate: true });

const onlyAllowNumbers = (event) => {
    const char = String.fromCharCode(event.which);
    if (!/[0-9]/.test(char)) event.preventDefault();
};

const setPrimary = (index) => {
    if (form.value.guardians[index].isPrimary) {
        form.value.guardians.forEach((g, i) => {
            if (i !== index) g.isPrimary = false;
        });
    }
};

const buildGuardianContact = (index) => {
    const g = form.value.guardians[index];
    return `${g.phoneCountryCode}${g.phoneNumber || ""}`;
};

const removeSecondary = () => {
    showSecondary.value = false;
    form.value.guardians[1] = { name: "", phoneCountryCode: "+94", phoneNumber: "", email: "", isPrimary: false };
    form.value.guardians[0].isPrimary = true;
};

function handleSubmit() {
    const g1 = form.value.guardians[0];
    const guardians = [{ name: g1.name, contactNumber: buildGuardianContact(0), email: g1.email, isPrimary: g1.isPrimary }];
    if (showSecondary.value) {
        const g2 = form.value.guardians[1];
        guardians.push({ name: g2.name, contactNumber: buildGuardianContact(1), email: g2.email, isPrimary: g2.isPrimary });
    }
    emit("update-prfile", { guardians });
}
</script>

<style scoped>
.input-field-container {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding-top: 0.4rem;
    padding-bottom: 1.1rem;
    position: relative;
}

.error-msg {
    color: #e24c4c;
    font-size: 0.7rem;
    position: absolute;
    margin-left: 0.2rem;
    bottom: 0;
    left: 0;
}

.add-link:hover {
    background-color: #f0f0f0;
    transition: background-color 0.4s ease;
}

.p-button-danger {
    min-width: 96px;
    height: 32px;
}

.phone-country-select {
    min-width: 140px;
    max-width: 180px;
}
</style>